import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Match } from './types';
import { supabase } from './utils';
import { Pane, Spinner, Select } from 'evergreen-ui';
import './Matches.css';
import useLoadAllData from './useLoadAllData';
import MatchComponent from './MatchComponent';
import Ribbon from './Ribbon';
import useStore from './useStore';

const Matches = () => {
  const componentRef = useRef(null);
  const { id: groupId } = useParams();

  const players = useStore((state) => state.players);
  const rounds = useStore((state) => state.rounds);
  const groups = useStore((state) => state.groups);
  const round_groups = useStore((state) => state.roundGroups);

  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState<Match[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>('');

  const { loading: AppLoading, loadAllData } = useLoadAllData();

  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMatches = () => {
    setLoading(true);
    supabase
      .from('matches')
      .select()
      .eq('groupId', groupId)
      .then((response) => {
        setMatches(response.data || []);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchMatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  if (loading || AppLoading) {
    return (
      <Pane>
        <Spinner marginX='auto' marginY={120} />
      </Pane>
    );
  }

  if (matches.length === 0) {
    return <div>No matches generated for selected group</div>;
  }

  const currentGroup = groups.find((g) => g.id === Number(groupId));
  const currentRoundId = round_groups.find(
    (cg) => cg.groupId === currentGroup?.id
  )?.roundId;

  const currentRound = rounds.find((r) => `${r.id}` === `${currentRoundId}`);

  // Get all unique teams from matches
  const getAllTeams = () => {
    const teamSet = new Set<string>();
    matches.forEach((match) => {
      if (match.team1) {
        const team1Players = match.team1.split(';').map((id) => {
          const player = players.find((p) => p.id === Number(id));
          return player ? player.name : id;
        });
        teamSet.add(team1Players.join(' & '));
      }
      if (match.team2) {
        const team2Players = match.team2.split(';').map((id) => {
          const player = players.find((p) => p.id === Number(id));
          return player ? player.name : id;
        });
        teamSet.add(team2Players.join(' & '));
      }
    });
    return Array.from(teamSet).sort();
  };

  // Filter matches based on selected team
  const getFilteredMatches = () => {
    if (!selectedTeam) return matches;

    return matches.filter((match) => {
      const team1Players = match.team1.split(';').map((id) => {
        const player = players.find((p) => p.id === Number(id));
        return player ? player.name : id;
      });
      const team2Players = match.team2.split(';').map((id) => {
        const player = players.find((p) => p.id === Number(id));
        return player ? player.name : id;
      });

      const team1Name = team1Players.join(' & ');
      const team2Name = team2Players.join(' & ');

      return team1Name === selectedTeam || team2Name === selectedTeam;
    });
  };

  const allTeams = getAllTeams();
  const filteredMatches = getFilteredMatches();

  return (
    <>
      <div className='home-page-image-container'>
        <img src='/logo.png' height='100px' alt='Logo'></img>
      </div>
      <Ribbon text={currentRound ? currentRound.name : ''} />
      <div className='matchesContainer'>
        <div className='printThis' ref={componentRef}>
          <div className='groupTitle'>
            <div className='matches-group-name'>
              {currentGroup?.name} scores
            </div>
            <div className='matches-group-text'>
              Number of matches: <b>{filteredMatches.length} </b>
            </div>
            <div className='matches-group-text'>
              Play to: <b>{currentGroup?.playTill}</b> | Win By:{' '}
              <b>{currentGroup?.winBy}</b>{' '}
            </div>
            {allTeams.length > 0 && (
              <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                <label style={{ marginRight: '10px', fontWeight: 'bold' }}>
                  Filter by Team:
                </label>
                <Select
                  value={selectedTeam}
                  onChange={(e: any) => setSelectedTeam(e.target.value)}
                  style={{ minWidth: '200px' }}
                >
                  <option value=''>All Teams</option>
                  {allTeams.map((team) => (
                    <option key={team} value={team}>
                      {team}
                    </option>
                  ))}
                </Select>
              </div>
            )}
          </div>
          <div className='printableContainer'>
            {filteredMatches
              .sort((a, b) => (a.id > b.id ? 1 : -1))
              .map((match) => (
                <MatchComponent
                  match={match}
                  players={players}
                  key={match.id}
                  groupId={groupId}
                  fetchMatches={fetchMatches}
                  disableSubmit={match.locked}
                  playTill={currentGroup?.playTill}
                  winBy={currentGroup?.winBy}
                />
              ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Matches;
